// Ambients.tsx
//
// Os ambientes vêm do `decryptedInfoToken`, não de HTTP — mesmo caminho de
// `Subsidiary.tsx` e `Items.tsx`, e pela mesma razão: o token já traz a travessia
// organization -> companies -> subsidiaries -> ambients, com `layout` e `items`
// de cada ambiente junto.
//
// O que estava aqui era um `setAmbientsData([...])` com "Salão Principal",
// "Cozinha" e "Área de Entrega" de uma "Pizzaria Gourmet" que não existe — três
// ambientes inventados, com mesas, cadeiras e "Wi-Fi Gratuito".
//
// CAMPOS QUE O MOCK INVENTOU e o dado real NÃO tem (conferido coluna a coluna em
// `acd."ActAmbient"`): `ambientImageUrl` e o `layout` no formato do mock
// (wallColor, floorType,
// tables, chairs, features, atmosphere). O `layout` real é uma linha de
// `acd."ActLayout"` — tem `layoutName` e `layoutInfo`, e nada de cor de parede.
//
// `ambientType` e `capacity` também eram campos do mock, mas VIRARAM COLUNAS de
// verdade: `garantirEstruturaDeSuprimentos` as acrescenta no boot do Central
// (ALTER TABLE ... ADD COLUMN IF NOT EXISTS) e o AmbientModal as cadastra. Elas
// continuam podendo vir vazias — ambiente antigo nunca as preencheu.
//
// Onde o mock preenchia campo sem coluna, a tela ESCONDE em vez de mostrar
// "N/A"/"0 mesas" para todo mundo: um vazio permanente é ruído, e foi
// exatamente o que fez esta tela mentir por tanto tempo.
import React, { useState, useMemo } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Ambients";
import LayoutEditor from "./LayoutEditor";
import { itemsService } from "../../../../services/item.service";
import { useCombinedStore } from "../../../../store";
import {
  TIPOS_DE_AMBIENTE_POR_NOME,
  APARENCIA_DE_TIPO_DESCONHECIDO,
} from "./tiposDeAmbiente";

interface AmbientsProps {
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
  subsidiary?: any;
}

const Ambients: React.FC<AmbientsProps> = ({
  decryptedInfoToken,
  handleOpenModal,
  subsidiary,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState(
    subsidiary ? subsidiary.subsidiaryUuid : "all"
  );
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");

  // Estado para controlar o LayoutEditor
  const [isLayoutEditorOpen, setIsLayoutEditorOpen] = useState(false);
  const [selectedAmbientForLayout, setSelectedAmbientForLayout] =
    useState<any>(null);

  const { authUserZustand, setAuthUserZustand } = useCombinedStore((state) => ({
    authUserZustand: state.authUserZustand,
    setAuthUserZustand: state.setAuthUserZustand,
  }));

  // Travessia organization -> companies -> subsidiaries -> ambients. O hook do
  // token pode devolver null no primeiro render, então tudo com `?.`.
  //
  // `storeName` e `companyName` são carregados junto porque os filtros e os
  // cartões mostram a que loja o ambiente pertence — no mock eles vinham
  // escritos à mão em cada objeto.
  const ambientsData: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).flatMap((filial: any) =>
        (filial?.ambients ?? []).map((ambiente: any) => ({
          ...ambiente,
          storeName: filial?.subsidiaryName,
          companyName: empresa?.companyName,
        }))
      )
    );
  }, [decryptedInfoToken]);

  /** As lojas reais, para o seletor "Todas as lojas". */
  const lojas: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) => empresa?.subsidiaries ?? []);
  }, [decryptedInfoToken]);

  const isLoading = !decryptedInfoToken;

  // Handler para abrir o Layout Editor
  const handleOpenLayoutEditor = (ambient: any) => {
    setSelectedAmbientForLayout(ambient);
    setIsLayoutEditorOpen(true);
  };

  // Handler para fechar o Layout Editor
  const handleCloseLayoutEditor = () => {
    setIsLayoutEditorOpen(false);
    setSelectedAmbientForLayout(null);
  };

  // Handler para salvar o layout
  //
  // NÃO SALVA, e agora DIZ isso. O que existia aqui era: chamada ao backend
  // comentada, mutação do array local do mock, e um alert
  // "Layout salvo com sucesso!". Ou seja, o dono mexia no layout do salão, lia
  // "salvo", saía da tela e perdia tudo — sem nunca saber.
  //
  // Com o mock removido, a mutação local nem faria sentido (a lista vem do
  // token). Então o alert passa a contar a verdade. Salvar de fato depende de um
  // `PATCH` de layout de salão no Central, que não existe: `ActLayout` só é
  // escrito hoje pelo editor de layout de COMANDA (prefixo "comanda:"), que tem
  // rota própria. É trabalho de outro card.
  const handleSaveLayout = async (layoutData: any) => {
    console.log("Layout do salão (não persistido):", layoutData);
    handleCloseLayoutEditor();
    alert(
      "O editor de layout do salão ainda não grava no servidor — o desenho " +
        "não foi salvo. (O layout de COMANDA, esse sim, é salvo pela tela de " +
        "Layout de Comanda.)"
    );
  };

  // Filtrar ambientes
  const filteredAmbients = ambientsData.filter((ambient) => {
    const matchesSearch =
      ambient.ambientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ambient.ambientDescription
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      ambient.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ambient.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && ambient.ambientStatus) ||
      (statusFilter === "inactive" && !ambient.ambientStatus);

    const matchesStore =
      storeFilter === "all" || ambient.subsidiary === storeFilter;

    return matchesSearch && matchesStatus && matchesStore;
  });

  const getStatusBadge = (status: boolean) => (
    <AlfredoStyles.StatusBadge $isActive={status}>
      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
        {status ? "check_circle" : "cancel"}
      </AlfredoStyles.MaterialIcons>
      {status ? "Ativo" : "Inativo"}
    </AlfredoStyles.StatusBadge>
  );

  // `ambientType` e `capacity` já são colunas de `acd."ActAmbient"`, mas só o
  // que foi cadastrado pelo AmbientModal as tem preenchidas. Sem valor, o selo
  // não é desenhado (em vez de um "undefined pessoas").
  const getTypeBadge = (type?: string) => {
    if (!type) return null;
    // A lista mora em `tiposDeAmbiente.ts`: o AmbientModal oferece as mesmas
    // cinco opções, e uma cópia aqui faria o selo divergir do cadastro.
    const typeConfig: any = TIPOS_DE_AMBIENTE_POR_NOME;

    const config = typeConfig[type] || APARENCIA_DE_TIPO_DESCONHECIDO;

    return (
      <AlfredoStyles.TypeBadge $color={config.color}>
        <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
          {config.icon}
        </AlfredoStyles.MaterialIcons>
        {type}
      </AlfredoStyles.TypeBadge>
    );
  };

  const getCapacityBadge = (capacity?: number) =>
    !capacity ? null : (
    <AlfredoStyles.CapacityBadge>
      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-xs)" }}>
        people
      </AlfredoStyles.MaterialIcons>
      {capacity} pessoas
    </AlfredoStyles.CapacityBadge>
  );

  const getItemsCount = (ambient: any) => (
    <AlfredoStyles.ItemsBadge>
      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-xs)" }}>
        inventory_2
      </AlfredoStyles.MaterialIcons>
      {ambient.items?.length || 0} itens
    </AlfredoStyles.ItemsBadge>
  );

  /** No dado real a coluna é `ambientLogoUrl`; `ambientImageUrl` era do mock. */
  const imagemDoAmbiente = (ambient: any): string | null =>
    getAmbientImage(ambient?.ambientLogoUrl ?? ambient?.ambientImageUrl);

  const getAmbientImage = (imageUrl?: string | null) => {
    if (imageUrl && imageUrl.startsWith("http")) {
      return imageUrl;
    } else if (imageUrl && imageUrl.startsWith("data:")) {
      return imageUrl;
    } else if (imageUrl) {
      return `data:image/jpeg;base64,${imageUrl}`;
    }
    return null;
  };

  const getLayoutPreview = (ambient: any) => {
    const image = imagemDoAmbiente(ambient);

    if (image) {
      return (
        <AlfredoStyles.AmbientImage src={image} alt={ambient.ambientName} />
      );
    }

    // O `layout` real é uma linha de `acd."ActLayout"`: tem `layoutName` e
    // `layoutInfo`, e NÃO tem `wallColor` nem `tables` (eram do mock). Sem
    // imagem, o cartão mostra o nome do layout — ou diz que não há —, em vez de
    // desenhar "0 mesas" numa parede cinza inventada para todo ambiente.
    return (
      <AlfredoStyles.LayoutPreview $wallColor="#f8f9fa">
        <AlfredoStyles.LayoutInfo>
          {ambient.layout?.layoutName || "Sem layout de salão"}
        </AlfredoStyles.LayoutInfo>
      </AlfredoStyles.LayoutPreview>
    );
  };

  const renderGridView = () => (
    <AlfredoStyles.AmbientsGrid>
      {filteredAmbients.map((ambient) => (
        <AlfredoStyles.AmbientCard
          key={ambient.ambientUuid}
          onClick={() => handleOpenModal("ambient", ambient)}
        >
          {getLayoutPreview(ambient)}

          <AlfredoStyles.CardContent>
            <AlfredoStyles.CardTitleRow>
              <AlfredoStyles.CardTitle>
                {ambient.ambientName}
              </AlfredoStyles.CardTitle>
              {getStatusBadge(ambient.ambientStatus)}
            </AlfredoStyles.CardTitleRow>

            <AlfredoStyles.CardDescription>
              {ambient.ambientDescription || "Sem descrição"}
            </AlfredoStyles.CardDescription>

            <AlfredoStyles.CardBadges>
              {getTypeBadge(ambient.ambientType)}
              {getCapacityBadge(ambient.capacity)}
              {getItemsCount(ambient)}
            </AlfredoStyles.CardBadges>

            <AlfredoStyles.CardMeta>
              <AlfredoStyles.MetaItem>
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
                  store
                </AlfredoStyles.MaterialIcons>
                {ambient.storeName}
              </AlfredoStyles.MetaItem>
              <AlfredoStyles.MetaItem>
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
                  design_services
                </AlfredoStyles.MaterialIcons>
                {ambient.layout?.layoutName || "Sem layout"}
              </AlfredoStyles.MetaItem>
            </AlfredoStyles.CardMeta>

            <AlfredoStyles.CardActions>
              <AlfredoStyles.Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal("ambient", ambient);
                }}
              >
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  edit
                </AlfredoStyles.MaterialIcons>
                Editar
              </AlfredoStyles.Button>
              <AlfredoStyles.SecondaryButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenLayoutEditor(ambient);
                }}
              >
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  design_services
                </AlfredoStyles.MaterialIcons>
                Layout
              </AlfredoStyles.SecondaryButton>
            </AlfredoStyles.CardActions>
          </AlfredoStyles.CardContent>
        </AlfredoStyles.AmbientCard>
      ))}
    </AlfredoStyles.AmbientsGrid>
  );

  const renderListView = () => (
    <AlfredoStyles.AmbientsList>
      {filteredAmbients.map((ambient) => (
        <AlfredoStyles.AmbientListCard
          key={ambient.ambientUuid}
          onClick={() => handleOpenModal("ambient", ambient)}
        >
          <AlfredoStyles.ListCardHeader>
            <AlfredoStyles.ListCardLeft>
              {imagemDoAmbiente(ambient) ? (
                <AlfredoStyles.AmbientThumbnail
                  src={imagemDoAmbiente(ambient)!}
                  alt={ambient.ambientName}
                />
              ) : (
                <AlfredoStyles.AmbientThumbnailPlaceholder>
                  <AlfredoStyles.MaterialIcons style={{ fontSize: "calc(var(--u) * 28)" }}>
                    room
                  </AlfredoStyles.MaterialIcons>
                </AlfredoStyles.AmbientThumbnailPlaceholder>
              )}

              <AlfredoStyles.ListCardInfo>
                <AlfredoStyles.ListCardTitleRow>
                  <AlfredoStyles.CardTitle>
                    {ambient.ambientName}
                  </AlfredoStyles.CardTitle>
                  {getStatusBadge(ambient.ambientStatus)}
                  {getTypeBadge(ambient.ambientType)}
                  {getCapacityBadge(ambient.capacity)}
                  {getItemsCount(ambient)}
                </AlfredoStyles.ListCardTitleRow>

                <AlfredoStyles.CardDescription>
                  {ambient.ambientDescription || "Sem descrição"}
                </AlfredoStyles.CardDescription>

                <AlfredoStyles.ListCardMeta>
                  <AlfredoStyles.MetaItem>
                    <strong>Loja:</strong> {ambient.storeName}
                  </AlfredoStyles.MetaItem>
                  <AlfredoStyles.MetaItem>
                    <strong>Layout:</strong>{" "}
                    {ambient.layout?.layoutName || "Sem layout"}
                  </AlfredoStyles.MetaItem>
                </AlfredoStyles.ListCardMeta>
              </AlfredoStyles.ListCardInfo>
            </AlfredoStyles.ListCardLeft>

            <AlfredoStyles.ListCardActions>
              <AlfredoStyles.Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal("ambient", ambient);
                }}
              >
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  edit
                </AlfredoStyles.MaterialIcons>
                Editar
              </AlfredoStyles.Button>
              <AlfredoStyles.SecondaryButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenLayoutEditor(ambient);
                }}
              >
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  design_services
                </AlfredoStyles.MaterialIcons>
                Layout
              </AlfredoStyles.SecondaryButton>
            </AlfredoStyles.ListCardActions>
          </AlfredoStyles.ListCardHeader>

          {/* Itens do Ambiente */}
          {ambient.items && ambient.items.length > 0 && (
            <AlfredoStyles.ItemsSection>
              <AlfredoStyles.ItemsHeader>
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  inventory_2
                </AlfredoStyles.MaterialIcons>
                <span>Itens cadastrados:</span>
              </AlfredoStyles.ItemsHeader>
              <AlfredoStyles.ItemsGrid>
                {ambient.items.map((item: any, index: number) => (
                  <AlfredoStyles.ItemCard key={index}>
                    <AlfredoStyles.ItemName>
                      {item.itemName}
                    </AlfredoStyles.ItemName>
                    <AlfredoStyles.ItemFooter>
                      <AlfredoStyles.ItemType>
                        {itemsService.getItemTypeText(item.itemType)}
                      </AlfredoStyles.ItemType>
                      <AlfredoStyles.ItemStatus $isActive={item.itemStatus}>
                        {item.itemStatus ? "Ativo" : "Inativo"}
                      </AlfredoStyles.ItemStatus>
                    </AlfredoStyles.ItemFooter>
                  </AlfredoStyles.ItemCard>
                ))}
              </AlfredoStyles.ItemsGrid>
            </AlfredoStyles.ItemsSection>
          )}
        </AlfredoStyles.AmbientListCard>
      ))}
    </AlfredoStyles.AmbientsList>
  );

  return (
    <>
      <AlfredoStyles.FieldsContainer>
        {/* Cabeçalho */}
        <AlfredoStyles.Header>
          <AlfredoStyles.HeaderContent>
            <AlfredoStyles.HeaderTitle>
              <AlfredoStyles.MaterialIcons style={{ fontSize: "calc(var(--u) * 36)" }}>
                room
              </AlfredoStyles.MaterialIcons>
              {subsidiary
                ? `Ambientes - ${subsidiary.subsidiaryName}`
                : "Ambientes"}
            </AlfredoStyles.HeaderTitle>
            <AlfredoStyles.HeaderSubtitle>
              {subsidiary
                ? `Configure os espaços físicos da loja ${subsidiary.subsidiaryName}`
                : "Organize os espaços físicos das suas lojas"}
            </AlfredoStyles.HeaderSubtitle>
          </AlfredoStyles.HeaderContent>

          <AlfredoStyles.HeaderActions>
            {/* Toggle View Mode */}
            <AlfredoStyles.ViewModeToggle>
              <AlfredoStyles.ViewModeButton
                $isActive={viewMode === "grid"}
                onClick={() => setViewMode("grid")}
              >
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  grid_view
                </AlfredoStyles.MaterialIcons>
                Grid
              </AlfredoStyles.ViewModeButton>
              <AlfredoStyles.ViewModeButton
                $isActive={viewMode === "list"}
                onClick={() => setViewMode("list")}
              >
                <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  view_list
                </AlfredoStyles.MaterialIcons>
                Lista
              </AlfredoStyles.ViewModeButton>
            </AlfredoStyles.ViewModeToggle>

            <AlfredoStyles.Button
              onClick={() =>
                handleOpenModal("ambient", { subsidiary: subsidiary })
              }
            >
              <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
              Novo Ambiente
            </AlfredoStyles.Button>
          </AlfredoStyles.HeaderActions>
        </AlfredoStyles.Header>

        {/* Filtros e Busca */}
        <AlfredoStyles.FilterSection>
          <AlfredoStyles.SearchWrapper>
            <AlfredoStyles.SearchIcon>
              <AlfredoStyles.MaterialIcons>search</AlfredoStyles.MaterialIcons>
            </AlfredoStyles.SearchIcon>
            <AlfredoStyles.SearchInput
              type="text"
              placeholder="Buscar por nome, descrição, loja..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <AlfredoStyles.ClearButton onClick={() => setSearchTerm("")}>
                <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              </AlfredoStyles.ClearButton>
            )}
          </AlfredoStyles.SearchWrapper>

          <AlfredoStyles.FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </AlfredoStyles.FilterSelect>

          {/*
            O filtro por TIPO de ambiente continua fora. A coluna `ambientType`
            passou a existir, mas ela nasceu vazia em toda linha anterior ao
            AmbientModal: enquanto a base não tiver tipo cadastrado, escolher
            qualquer opção esvazia a lista — o mesmo filtro que só sabe esconder
            tudo. Volta quando houver ambiente com tipo gravado.
          */}

          {!subsidiary && (
            <AlfredoStyles.FilterSelect
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
            >
              <option value="all">Todas as lojas</option>
              {/* As lojas reais do token — antes eram duas pizzarias inventadas. */}
              {lojas.map((loja) => (
                <option key={loja.subsidiaryUuid} value={loja.subsidiaryUuid}>
                  {loja.subsidiaryName}
                </option>
              ))}
            </AlfredoStyles.FilterSelect>
          )}
        </AlfredoStyles.FilterSection>

        {/* Estatísticas */}
        <AlfredoStyles.StatsGrid>
          <AlfredoStyles.StatCard $color="#0066cc">
            <AlfredoStyles.StatIcon>🏠</AlfredoStyles.StatIcon>
            <AlfredoStyles.StatValue>
              {ambientsData.length}
            </AlfredoStyles.StatValue>
            <AlfredoStyles.StatLabel>
              Total de Ambientes
            </AlfredoStyles.StatLabel>
          </AlfredoStyles.StatCard>

          <AlfredoStyles.StatCard $color="#28a745">
            <AlfredoStyles.StatIcon>✅</AlfredoStyles.StatIcon>
            <AlfredoStyles.StatValue>
              {ambientsData.filter((a) => a.ambientStatus).length}
            </AlfredoStyles.StatValue>
            <AlfredoStyles.StatLabel>Ambientes Ativos</AlfredoStyles.StatLabel>
          </AlfredoStyles.StatCard>

          <AlfredoStyles.StatCard $color="#ffc107">
            <AlfredoStyles.StatIcon>📦</AlfredoStyles.StatIcon>
            <AlfredoStyles.StatValue>
              {ambientsData.reduce(
                (acc, ambient) => acc + (ambient.items?.length || 0),
                0
              )}
            </AlfredoStyles.StatValue>
            <AlfredoStyles.StatLabel>Itens Cadastrados</AlfredoStyles.StatLabel>
          </AlfredoStyles.StatCard>

          <AlfredoStyles.StatCard $color="#dc3545">
            <AlfredoStyles.StatIcon>👥</AlfredoStyles.StatIcon>
            <AlfredoStyles.StatValue>
              {ambientsData.reduce(
                (acc, ambient) => acc + (ambient.capacity || 0),
                0
              )}
            </AlfredoStyles.StatValue>
            <AlfredoStyles.StatLabel>Capacidade Total</AlfredoStyles.StatLabel>
          </AlfredoStyles.StatCard>
        </AlfredoStyles.StatsGrid>

        {/* Lista de Ambientes */}
        {isLoading ? (
          <AlfredoStyles.LoadingContainer>
            <AlfredoStyles.LoadingSpinner />
            <div>Carregando ambientes...</div>
          </AlfredoStyles.LoadingContainer>
        ) : filteredAmbients.length > 0 ? (
          viewMode === "list" ? (
            renderListView()
          ) : (
            renderGridView()
          )
        ) : (
          <AlfredoStyles.EmptyState>
            <AlfredoStyles.EmptyStateIcon>🏠</AlfredoStyles.EmptyStateIcon>
            <AlfredoStyles.EmptyStateTitle>
              {searchTerm
                ? "Nenhum ambiente encontrado"
                : "Nenhum ambiente cadastrado"}
            </AlfredoStyles.EmptyStateTitle>
            <AlfredoStyles.EmptyStateDescription>
              {searchTerm
                ? "Tente ajustar os filtros de busca"
                : "Comece criando seu primeiro ambiente"}
            </AlfredoStyles.EmptyStateDescription>
            {!searchTerm && (
              <AlfredoStyles.Button
                onClick={() => handleOpenModal("ambient")}
                style={{ marginTop: "var(--sp-16)" }}
              >
                <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
                Criar Primeiro Ambiente
              </AlfredoStyles.Button>
            )}
          </AlfredoStyles.EmptyState>
        )}

        {/* Contador de resultados */}
        {!isLoading && filteredAmbients.length > 0 && (
          <AlfredoStyles.ResultsCounter>
            Mostrando <strong>{filteredAmbients.length}</strong> de{" "}
            <strong>{ambientsData.length}</strong> ambiente
            {ambientsData.length !== 1 ? "s" : ""}
          </AlfredoStyles.ResultsCounter>
        )}
      </AlfredoStyles.FieldsContainer>

      {/* Modal do Layout Editor */}
      {isLayoutEditorOpen && selectedAmbientForLayout && (
        <AlfredoStyles.ModalOverlay onClick={handleCloseLayoutEditor}>
          <AlfredoStyles.ModalContainer onClick={(e) => e.stopPropagation()}>
            <LayoutEditor
              ambient={selectedAmbientForLayout}
              onSave={handleSaveLayout}
              onClose={handleCloseLayoutEditor}
            />
          </AlfredoStyles.ModalContainer>
        </AlfredoStyles.ModalOverlay>
      )}
    </>
  );
};

export default Ambients;
